import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { 
  CreditCard, 
  Plane, 
  Gift, 
  Building2, 
  ArrowRight, 
  Zap, 
  CheckCircle, 
  Tag, 
  ChevronDown, 
  Shield, 
  Wallet, 
  Percent, 
  ListFilter, 
  X, 
  Info, 
  ExternalLink, 
  Search, 
  Users, 
  Sparkles, 
  Crown, 
  ArrowUpRight, 
  Bell, 
  ChevronLeft, 
  ChevronRight, 
  HelpCircle, 
  AlertCircle, 
  BanknoteIcon,
  StarOff as StarOutline 
} from 'lucide-react';
import FAQ from '../components/FAQ';
import CardFilters from '../components/CardFilters';
import FeaturedCards from '../components/FeaturedCards';

interface CreditCard {
  id: string;
  name: string;
  bank: string;
  logo: string;
  cardImage: string;
  type: string;
  features: {
    category: string;
    items: {
      title: string;
      value: string;
    }[];
  }[];
  benefits: string[];
  annualFee: string;
  cardType: 'Bireysel Kart' | 'Ticari Kart';
  isSponsored?: boolean;
  color: string;
  applicationUrl: string;
  campaign?: {
    title: string;
    description: string;
    endDate: string;
  };
}

const creditCards: CreditCard[] = [
  {
    id: 'miles-platinum',
    name: 'Miles&Smiles Platinum',
    bank: 'Garanti BBVA',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png',
    cardImage: 'https://images.unsplash.com/photo-1614623658831-61644f04e316?auto=format&fit=crop&q=80&w=500&h=300',
    type: 'Mil Kazandıran',
    features: [
      {
        category: 'Mil Kazanımı',
        items: [
          { title: 'Yurt İçi Alışveriş', value: '%1.5' },
          { title: 'Yurt Dışı Alışveriş', value: '%2' },
          { title: 'Hoş Geldin Bonus', value: '5000 Mil' }
        ]
      },
      {
        category: 'Ücretler',
        items: [
          { title: 'Yıllık Kart Ücreti', value: '1131 ₺' },
          { title: 'Ek Kart Ücreti', value: '565 ₺' },
          { title: 'Kart Yenileme', value: 'Ücretsiz' }
        ]
      },
      {
        category: 'Seyahat Avantajları',
        items: [
          { title: 'Lounge Giriş', value: '2 Kişi Ücretsiz' },
          { title: 'Seyahat Sigortası', value: 'Limitsiz' },
          { title: 'Araç Kiralama', value: '%25 İndirim' }
        ]
      }
    ],
    benefits: [
      'Yurt dışı harcamalarınıza ekstra 2.000 mil',
      'Ücretsiz lounge erişimi',
      'Seyahat sigortası',
      'Restoranlarda %10 indirim',
      'Havalimanı transfer indirimi'
    ],
    annualFee: '1131 ₺',
    cardType: 'Bireysel Kart',
    isSponsored: true,
    color: 'purple',
    applicationUrl: 'https://www.garantibbva.com.tr/kartlar/kredi-kartlari',
    campaign: {
      title: 'Hoş Geldin Kampanyası',
      description: 'İlk ay 10.000 TL harcamaya 5.000 mil',
      endDate: '31.12.2024'
    }
  },
  {
    id: 'world-elite',
    name: 'World Elite',
    bank: 'Yapı Kredi',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png',
    cardImage: 'https://images.unsplash.com/photo-1511938736117-4b485e52c9bb?auto=format&fit=crop&q=80&w=500&h=300',
    type: 'Puan Kazandıran',
    features: [
      {
        category: 'Puan Kazanımı',
        items: [
          { title: 'Standart Alışveriş', value: '%1.2' },
          { title: 'Market Alışverişi', value: '%2.5' },
          { title: 'Hoş Geldin Bonus', value: '120.000 Puan' }
        ]
      },
      {
        category: 'Ücretler',
        items: [
          { title: 'Yıllık Kart Ücreti', value: '985 ₺' },
          { title: 'Ek Kart Ücreti', value: '492 ₺' },
          { title: 'Kart Yenileme', value: 'Ücretsiz' }
        ]
      },
      {
        category: 'Özel Avantajlar',
        items: [
          { title: 'Concierge', value: '7/24 Hizmet' },
          { title: 'Asistans', value: 'Premium Paket' },
          { title: 'Özel Etkinlikler', value: 'Öncelikli Katılım' }
        ]
      }
    ],
    benefits: [
      'Tüm restoranlarda %5 World Puan',
      'Seçili markalarda 12 taksit',
      'Ücretsiz vale hizmeti',
      'Premium concierge hizmetleri',
      'Özel etkinlik ve davetlere katılım'
    ],
    annualFee: '985 ₺',
    cardType: 'Bireysel Kart',
    color: 'red',
    applicationUrl: 'https://www.yapikredi.com.tr/kartlar/kredi-kartlari/',
    campaign: {
      title: 'Yeni Üye Kampanyası',
      description: 'İlk alışverişinize 1.000 TL WorldPuan',
      endDate: '31.12.2024'
    }
  },
  {
    id: 'maximum-black',
    name: 'Maximum Black',
    bank: 'İş Bankası',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png',
    cardImage: 'https://images.unsplash.com/photo-1601583789200-96cd7f385315?auto=format&fit=crop&q=80&w=500&h=300',
    type: 'Puan Kazandıran',
    features: [
      {
        category: 'Puan Kazanımı',
        items: [
          { title: 'Her Alışveriş', value: '%1.8' },
          { title: 'Akaryakıt', value: '%3' },
          { title: 'Hoş Geldin Bonus', value: '90.000 Puan' }
        ]
      },
      {
        category: 'Ücretler',
        items: [
          { title: 'Yıllık Kart Ücreti', value: '892 ₺' },
          { title: 'Ek Kart Ücreti', value: '446 ₺' },
          { title: 'Kart Yenileme', value: 'Ücretsiz' }
        ]
      },
      {
        category: 'Premium Hizmetler',
        items: [
          { title: 'Lounge Giriş', value: '4 Kez/Yıl' },
          { title: 'Araç Asistans', value: 'Limitsiz' },
          { title: 'Kaza Sigortası', value: '500.000 ₺' }
        ]
      }
    ],
    benefits: [
      'Akaryakıtta %3 MaxiPuan',
      'Seçili restoranlarda %10 indirim',
      'Ücretsiz otopark hizmeti',
      'Havalimanı transfer indirimi',
      'Yurt dışı alışverişlerde ekstra puan'
    ],
    annualFee: '892 ₺',
    cardType: 'Bireysel Kart',
    color: 'blue',
    isSponsored: true,
    applicationUrl: 'https://www.isbank.com.tr/kartlar',
    campaign: {
      title: 'Maximum Fırsat',
      description: 'Seçili kategorilerde 3 kata varan MaxiPuan',
      endDate: '31.12.2024'
    }
  },
  {
    id: 'wings-card',
    name: 'Wings Card',
    bank: 'Akbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/7accd65f-06ae-48b1-875e-6d9827590be6.png',
    cardImage: 'https://images.unsplash.com/photo-1574421233776-31c84eb9e4db?auto=format&fit=crop&q=80&w=500&h=300',
    type: 'Mil Kazandıran',
    features: [
      {
        category: 'Mil Kazanımı',
        items: [
          { title: 'Yurt İçi Alışveriş', value: '%1.3' },
          { title: 'Seyahat Harcamaları', value: '%2.5' },
          { title: 'Hoş Geldin Bonus', value: '7500 Mil' }
        ]
      },
      {
        category: 'Ücretler',
        items: [
          { title: 'Yıllık Kart Ücreti', value: '945 ₺' },
          { title: 'Ek Kart Ücreti', value: '472 ₺' },
          { title: 'Kart Yenileme', value: 'Ücretsiz' }
        ]
      },
      {
        category: 'Seyahat Ayrıcalıkları',
        items: [
          { title: 'Lounge Giriş', value: '6 Kez/Yıl' },
          { title: 'Mil Programı', value: 'Star Alliance' },
          { title: 'Seyahat Sigortası', value: '750.000 ₺' }
        ]
      }
    ],
    benefits: [
      'Tüm havayollarında geçerli mil programı',
      'Ücretsiz havalimanı transferi',
      'Seyahat ve bagaj sigortası',
      'Fast Track geçiş hakkı',
      'Valet park indirimi'
    ],
    annualFee: '945 ₺',
    cardType: 'Bireysel Kart',
    color: 'red',
    applicationUrl: 'https://www.akbank.com/kartlar/kredi-kartlari',
    campaign: {
      title: 'Wings Kampanyası',
      description: 'İlk uçak bileti alımında 2.500 bonus mil',
      endDate: '31.12.2024'
    }
  }
];

const filters = {
  annualFee: ['Ücretsiz', '0-500 TL', '501-1000 TL', '1000 TL üzeri'],
  cardType: ['Bireysel Kart', 'Ticari Kart'],
  bank: ['Garanti BBVA', 'Yapı Kredi', 'İş Bankası', 'Akbank', 'QNB Finansbank'],
  features: ['Ücretsiz Havalimanı Lounge', 'Seyahat Sigortası', 'Araç Kiralama İndirimi', 'Restoran İndirimleri'],
  cardCategory: ['Mil Kazandıran', 'Puan Kazandıran', 'Nakit İade']
};

interface FeaturedCardDB {
  id: string;
  bank_id: string;
  bank_name: string;
  bank_logo: string;
  card_name: string;
  description: string;
  annual_fee: string;
  cashback?: string;
  welcome_bonus?: string;
  additional_card_fee?: string;
  features: string[];
  benefits: string[];
  campaign_title?: string;
  campaign_description?: string;
  redirect_url: string;
  is_featured: boolean;
  is_active: boolean;
}

const KrediKartlari = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dbCards, setDbCards] = useState<CreditCard[]>([]);
  const [selectedFilters, setSelectedFilters] = useState({
    annualFee: [],
    cardType: [],
    bank: [],
    features: [],
    cardCategory: []
  });

  useEffect(() => {
    const fetchDbCards = async () => {
      try {
        const { data, error } = await supabase
          .from('featured_cards')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });
        if (error || !data) return;
        const mapped: CreditCard[] = (data as FeaturedCardDB[]).map(db => ({
          id: db.id,
          name: db.card_name,
          bank: db.bank_name,
          logo: db.bank_logo,
          cardImage: '',
          type: db.cashback?.includes('Mil') ? 'Mil Kazandıran' : 'Puan Kazandıran',
          features: [
            { category: 'Kazanım', items: [
              { title: 'Puan/Mil Kazanımı', value: db.cashback || '%1.5' },
              { title: 'Hoş Geldin Bonusu', value: db.welcome_bonus || '-' },
              { title: 'Ek Kart Ücreti', value: db.additional_card_fee || '-' }
            ] },
            { category: 'Ücretler', items: [
              { title: 'Yıllık Kart Ücreti', value: db.annual_fee }
            ] }
          ],
          benefits: db.benefits || [],
          annualFee: db.annual_fee,
          cardType: 'Bireysel Kart',
          color: 'blue',
          applicationUrl: db.redirect_url,
          campaign: db.campaign_title ? { title: db.campaign_title, description: db.campaign_description || '', endDate: '31.12.2024' } : undefined
        }));
        setDbCards(mapped);
      } catch {}
    };
    fetchDbCards();
  }, []);
  const handleFilterChange = (category: string, value: string) => {
    setSelectedFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(v => v !== value)
        : [...prev[category], value]
    }));
  };

  const clearFilters = () => {
    setSelectedFilters({
      annualFee: [],
      cardType: [],
      bank: [],
      features: [],
      cardCategory: []
    });
    setSearchTerm('');
  };

  const featureKeywords: Record<string, string[]> = {
    'Ücretsiz Havalimanı Lounge': ['lounge'],
    'Seyahat Sigortası': ['seyahat sigortası'],
    'Araç Kiralama İndirimi': ['araç kiralama'],
    'Restoran İndirimleri': ['restoran']
  };

  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase('tr-TR');
  const hasActiveFilters = normalizedSearchTerm.length > 0 || Object.values(selectedFilters).some(values => values.length > 0);

  const allCards = [...creditCards, ...dbCards];
  const filteredCards = allCards.filter(card => {
    if (normalizedSearchTerm) {
      const searchableText = [card.name, card.bank, card.type, ...card.benefits]
        .join(' ')
        .toLocaleLowerCase('tr-TR');
      if (!searchableText.includes(normalizedSearchTerm)) return false;
    }

    if (selectedFilters.bank.length && !selectedFilters.bank.includes(card.bank)) return false;
    if (selectedFilters.cardType.length && !selectedFilters.cardType.includes(card.cardType)) return false;
    if (selectedFilters.cardCategory.length && !selectedFilters.cardCategory.includes(card.type)) return false;

    if (selectedFilters.annualFee.length) {
      const fee = parseInt(card.annualFee.replace(/[^0-9]/g, ''));
      const matchesRange = selectedFilters.annualFee.some(range => {
        if (range === 'Ücretsiz') return fee === 0;
        if (range === '0-500 TL') return fee > 0 && fee <= 500;
        if (range === '501-1000 TL') return fee > 500 && fee <= 1000;
        if (range === '1000 TL üzeri') return fee > 1000;
        return false;
      });
      if (!matchesRange) return false;
    }

    if (selectedFilters.features.length) {
      const cardFeatures = card.benefits.map(benefit => benefit.toLocaleLowerCase('tr-TR'));
      const hasAllFeatures = selectedFilters.features.every(feature =>
        (featureKeywords[feature] || [feature.toLocaleLowerCase('tr-TR')]).some(keyword =>
          cardFeatures.some(cardFeature => cardFeature.includes(keyword))
        )
      );
      if (!hasAllFeatures) return false;
    }

    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-b from-[#F7BA45]/20 to-slate-50 py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-4 md:mb-6">
              Kredi Kartları
            </h1>
            <p className="text-sm md:text-base text-slate-600 mb-8 md:mb-12">
              Size en uygun kredi kartını seçin, avantajlardan yararlanın
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="lg:w-80 flex-shrink-0">
              <CardFilters
                filters={filters}
                selectedFilters={selectedFilters}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
              />
            </div>

            <div className="flex-1">
              {!hasActiveFilters && <FeaturedCards />}

              <div className="space-y-4">
                {filteredCards.map((card: CreditCard) => (
                  <div
                    key={card.id}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="p-4 md:p-6">
                      <div className="flex items-center justify-between mb-4 md:mb-6">
                        <div className="flex items-center gap-3 md:gap-4">
                          <img 
                            src={card.logo} 
                            alt={card.bank} 
                            className="h-8 md:h-12 object-contain"
                          />
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-lg md:text-xl font-semibold text-slate-800">{card.name}</h3>
                            </div>
                            <p className="text-xs md:text-sm text-slate-600">{card.type}</p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
                        <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                          <div className="text-xs md:text-sm text-slate-500 mb-1">Yıllık Ücret</div>
                          <div className="font-semibold text-slate-800 text-sm md:text-base">{card.annualFee}</div>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                          <div className="text-xs md:text-sm text-slate-500 mb-1">Puan/Mil Kazanımı</div>
                          <div className="font-semibold text-slate-800 text-sm md:text-base">{card.features[0]?.items?.[0]?.value ?? '-'}</div>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                          <div className="text-xs md:text-sm text-slate-500 mb-1">Hoş Geldin Bonusu</div>
                          <div className="font-semibold text-slate-800 text-sm md:text-base">{card.features[0]?.items?.[2]?.value ?? '-'}</div>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                          <div className="text-xs md:text-sm text-slate-500 mb-1">Ek Kart Ücreti</div>
                          <div className="font-semibold text-slate-800 text-sm md:text-base">{card.features[1]?.items?.[1]?.value ?? '-'}</div>
                        </div>
                      </div>

                      {card.campaign && (
                        <div className="mb-4 md:mb-6 bg-gradient-to-r from-[#F7BA45]/10 to-transparent rounded-xl p-4">
                          <div className="flex items-start gap-3">
                            <Zap size={20} className="text-[#F7BA45] flex-shrink-0" />
                            <div>
                              <h4 className="font-medium text-slate-900 mb-1">{card.campaign.title}</h4>
                              <p className="text-sm text-slate-600">{card.campaign.description}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-2 text-xs md:text-sm text-slate-600">
                          <Info size={16} className="text-[#F7BA45]" />
                          <span>Başvuru için {card.bank}'a yönlendirilirsiniz</span>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                          <a href={card.applicationUrl} target="_blank" rel="noopener noreferrer" className="text-xs md:text-sm text-[#F7BA45] hover:underline whitespace-nowrap mb-3 sm:mb-0">
                            Detaylı Bilgi
                            <ExternalLink size={12} className="inline-block ml-1" />
                          </a>
                          <a
                            href={card.applicationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-4 md:px-6 py-2.5 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg text-sm md:text-base cursor-pointer"
                          >
                            HEMEN BAŞVUR
                            <ArrowRight size={16} className="hidden sm:block" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredCards.length === 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="font-medium text-slate-800">Aramanızla eşleşen kart bulunamadı.</p>
                    <p className="mt-2 text-sm text-slate-500">Farklı bir arama veya filtre deneyin.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">
                Sık Sorulan Sorular
              </h2>
              <p className="text-sm md:text-base text-slate-600 mb-8 md:mb-12">
                Kredi kartları hakkında merak ettiğiniz tüm sorular
              </p>
            </div>
            <FAQ />
          </div>
        </div>

    </div>
  );
};

export default KrediKartlari;